import type {
  DisciplineProgressReport,
  DisciplineProgressReportErrors,
  DisciplineProgressReportForm,
  DisciplineProgressReportPayload,
} from './interfaces'
import type {
  DisciplineProgressReportStatementKey,
  DisciplineProgressReportStatements,
  DisciplineProgressReportStatementsErrors,
  DisciplineProgressReportStatementsForm,
} from './types'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'

interface DisciplineProgressReportStatementRow {
  title: string
  text: string
}

const PROGRESS_REPORT_STATEMENT_KEYS: readonly DisciplineProgressReportStatementKey[] = [
  'statement_1',
  'statement_2',
  'statement_3',
  'statement_4',
]

// codestyle-ignore: date-format-enum — short month-day label; DateFormat has no month-day member (decided 2026-10-29)
const SHORT_DATE_FORMAT = 'MMM DD'
const LONG_DATE_FORMAT = DateFormat.FileDate

const hasText = (value: string | null | undefined): boolean => !!value?.trim()

export const getProgressReportStatementRows = (
  statements: DisciplineProgressReportStatements | null,
): DisciplineProgressReportStatementRow[] => PROGRESS_REPORT_STATEMENT_KEYS
  .map((key, index) => ({ title: `Statement ${index + 1}`, text: statements?.[key] ?? '' }))
  .filter(row => hasText(row.text))

/** As in the legacy report form: the year is written once when both ends fall in the same one. */
export const formatProgressReportDateRange = (from: string | null, to: string | null): string => {
  if (!from || !to) return '-'

  const dateFrom = dayjs(from)
  const dateTo = dayjs(to)
  const fromFormat = dateFrom.year() === dateTo.year() ? SHORT_DATE_FORMAT : LONG_DATE_FORMAT

  return `${dateFrom.format(fromFormat)} - ${dateTo.format(LONG_DATE_FORMAT)}`
}

const toStatementsForm = (statements: DisciplineProgressReportStatements | null): DisciplineProgressReportStatementsForm => ({
  statement_1: statements?.statement_1 ?? '',
  statement_2: statements?.statement_2 ?? '',
  statement_3: statements?.statement_3 ?? '',
  statement_4: statements?.statement_4 ?? '',
})

export const createProgressReportForm = (report: DisciplineProgressReport | null): DisciplineProgressReportForm => ({
  positiveTrait: report?.positive_trait ?? '',
  topics: report?.topics ?? '',
  progressGained: toStatementsForm(report?.progress_gained ?? null),
  toImprove: toStatementsForm(report?.to_improve ?? null),
  furtherSteps: toStatementsForm(report?.further_steps ?? null),
})

/** The legacy report form requires the first three statements of every group; the fourth is optional. */
const validateStatements = (statements: DisciplineProgressReportStatementsForm): DisciplineProgressReportStatementsErrors => ({
  statement_1: !hasText(statements.statement_1),
  statement_2: !hasText(statements.statement_2),
  statement_3: !hasText(statements.statement_3),
  statement_4: false,
})

const noStatementErrors = (): DisciplineProgressReportStatementsErrors => ({
  statement_1: false,
  statement_2: false,
  statement_3: false,
  statement_4: false,
})

export const emptyProgressReportErrors = (): DisciplineProgressReportErrors => ({
  positiveTrait: false,
  topics: false,
  progressGained: noStatementErrors(),
  toImprove: noStatementErrors(),
  furtherSteps: noStatementErrors(),
})

export const validateProgressReportForm = (form: DisciplineProgressReportForm): DisciplineProgressReportErrors => ({
  positiveTrait: !hasText(form.positiveTrait),
  topics: !hasText(form.topics),
  progressGained: validateStatements(form.progressGained),
  toImprove: validateStatements(form.toImprove),
  furtherSteps: validateStatements(form.furtherSteps),
})

export const hasStatementErrors = (errors: DisciplineProgressReportStatementsErrors): boolean =>
  Object.values(errors).some(Boolean)

export const hasProgressReportErrors = (errors: DisciplineProgressReportErrors): boolean =>
  errors.positiveTrait
  || errors.topics
  || hasStatementErrors(errors.progressGained)
  || hasStatementErrors(errors.toImprove)
  || hasStatementErrors(errors.furtherSteps)

const toStatementsPayload = (
  statements: DisciplineProgressReportStatementsForm,
): DisciplineProgressReportPayload['progress_gained'] => ({
  statement_1: statements.statement_1,
  statement_2: statements.statement_2,
  statement_3: hasText(statements.statement_3) ? statements.statement_3 : null,
  statement_4: hasText(statements.statement_4) ? statements.statement_4 : null,
})

export const buildProgressReportPayload = (form: DisciplineProgressReportForm): DisciplineProgressReportPayload => ({
  positive_trait: form.positiveTrait,
  topics: form.topics,
  progress_gained: toStatementsPayload(form.progressGained),
  to_improve: toStatementsPayload(form.toImprove),
  further_steps: toStatementsPayload(form.furtherSteps),
})
